import React, { useRef } from 'react';

function wrapSelection(textarea, before, after = before) {
  const start = textarea.selectionStart;
  const end = textarea.selectionEnd;
  const value = textarea.value;
  const selected = value.slice(start, end);
  const next = value.slice(0, start) + before + selected + after + value.slice(end);
  const cursorStart = start + before.length;
  const cursorEnd = cursorStart + selected.length;
  return { next, cursorStart, cursorEnd };
}

function insertAt(textarea, text) {
  const start = textarea.selectionStart;
  const end = textarea.selectionEnd;
  const value = textarea.value;
  const next = value.slice(0, start) + text + value.slice(end);
  return { next, cursorStart: start + text.length, cursorEnd: start + text.length };
}

function applyEdit(textarea, onChange, editFn) {
  const { next, cursorStart, cursorEnd } = editFn(textarea);
  onChange(next);
  requestAnimationFrame(() => {
    textarea.focus();
    textarea.setSelectionRange(cursorStart, cursorEnd);
  });
}

const TOOLBAR = [
  { key: 'bold', icon: 'fas fa-bold', title: 'Bold', wrap: ['<strong>', '</strong>'] },
  { key: 'italic', icon: 'fas fa-italic', title: 'Italic', wrap: ['<em>', '</em>'] },
  { key: 'highlight', icon: 'fas fa-highlighter', title: 'Highlight', wrap: ['<span class="highlight">', '</span>'] },
  { key: 'code', icon: 'fas fa-code', title: 'Inline code', wrap: ['<code>', '</code>'] },
];

export default function RichTextField({
  label,
  value = '',
  onChange,
  rows = 4,
  placeholder = '',
  hint = 'Use the toolbar for links, bold, and highlights. HTML is supported.',
  multilineParagraphs = false,
}) {
  const ref = useRef(null);

  const handleLink = () => {
    const textarea = ref.current;
    if (!textarea) return;
    const url = window.prompt('Link URL (include https:// for external links)', 'https://');
    if (!url) return;
    applyEdit(textarea, onChange, (ta) => wrapSelection(ta, `<a href="${url}" target="_blank" rel="noopener noreferrer">`, '</a>'));
  };

  const handleParagraph = () => {
    const textarea = ref.current;
    if (!textarea) return;
    applyEdit(textarea, onChange, (ta) => insertAt(ta, '<br />'));
  };

  return (
    <label className="tpb-rich-field">
      {label}
      <div className="tpb-rich-toolbar">
        {TOOLBAR.map((item) => (
          <button
            key={item.key}
            type="button"
            className="tpb-rich-toolbar-btn"
            title={item.title}
            onClick={() => {
              const textarea = ref.current;
              if (!textarea) return;
              applyEdit(textarea, onChange, (ta) => wrapSelection(ta, item.wrap[0], item.wrap[1]));
            }}
          >
            <i className={item.icon} aria-hidden="true" />
          </button>
        ))}
        <button type="button" className="tpb-rich-toolbar-btn" title="Insert link" onClick={handleLink}>
          <i className="fas fa-link" aria-hidden="true" />
        </button>
        <button type="button" className="tpb-rich-toolbar-btn" title="Line break" onClick={handleParagraph}>
          <i className="fas fa-level-down-alt" aria-hidden="true" />
        </button>
      </div>
      <textarea
        ref={ref}
        rows={rows}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
      {hint && <span className="tpb-rich-hint">{hint}</span>}
      {multilineParagraphs && (
        <span className="tpb-rich-hint">Separate paragraphs with a blank line.</span>
      )}
      {value && /<[a-z][\s\S]*>/i.test(value) && (
        <div className="tpb-rich-preview">
          <span className="tpb-rich-preview-label">Preview</span>
          <div className="tool-rich-content" dangerouslySetInnerHTML={{ __html: value }} />
        </div>
      )}
    </label>
  );
}

export function RichTextParagraphsField({ label, paragraphs = [], onChange, rows = 5 }) {
  const joined = (paragraphs || []).join('\n\n');

  return (
    <RichTextField
      label={label}
      value={joined}
      rows={rows}
      multilineParagraphs
      hint="Use the toolbar for links and styling. Separate paragraphs with a blank line."
      onChange={(text) => onChange(text.split('\n\n'))}
    />
  );
}
